import React, { useState, type FC } from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  TextField,
  Button,
  Box,
  Stack
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './translations.json';

type SupportedLanguage = keyof typeof translations;

export type QcmAnswers = Record<string, string>;

export interface Question {
  id: number;
  question: string;
}

interface AssesmentFormProps {
  offer: {
    title: string;
    company: string;
  };
  questions: Question[];
  onSubmit?: (answers: QcmAnswers) => Promise<void>;
}

const DARK_CARD_BG = '#121824';
const DARK_BORDER = '#1e2638';
const INPUT_BG = '#161d2b';

const TechnicalAssessmentForm: FC<AssesmentFormProps> = ({
  offer,
  questions,
  onSubmit
}) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [answers, setAnswers] = useState<QcmAnswers>({});

  const totalSteps = questions.length;
  const currentQuestion = questions[currentStepIndex];
  const currentAnswer = answers[currentQuestion?.question] || '';
  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.question]: value
    }));
  };

  const handleNext = async () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      if (onSubmit) {
        setLoading(true);
        await onSubmit(answers);
        setLoading(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  if (!currentQuestion) return null;

  return (
    <Card
      sx={{
        maxWidth: 680,
        width: '100%',
        mx: 'auto',
        borderRadius: '16px',
        backgroundColor: DARK_CARD_BG,
        borderColor: DARK_BORDER,
        borderWidth: 1,
        borderStyle: 'solid',
        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)',
        p: { xs: 1, sm: 2 }
      }}
    >
      <CardContent>
        {/* Header: Step & Percentage */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="body2" sx={{ color: '#8c94a4', fontSize: '0.875rem' }}>
            {content.step} {currentStepIndex + 1} {content.of} {totalSteps}
          </Typography>
          <Typography variant="body2" sx={{ color: '#8c94a4', fontSize: '0.875rem', fontWeight: 600 }}>
            {progressPercent}%
          </Typography>
        </Box>

        {/* Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: INPUT_BG,
            mb: 3.5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'primary.main',
              borderRadius: 3,
              transition: 'transform 0.4s ease'
            }
          }}
        />

        {/* Main Header */}
        <Typography variant="h5" component="h1" fontWeight={700} color="#ffffff" gutterBottom>
          {content.title}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600} color="#3b82f6">
          {offer?.title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#8c94a4' }} mb={3.5}>
          {offer?.company}
        </Typography>

        {/* Question Title */}
        <Typography variant="subtitle1" fontWeight={600} color="#ffffff" mb={1.5}>
          {currentQuestion.question}
        </Typography>

        {/* Text Input Field */}
        <Typography variant="subtitle2" fontWeight={600} color="#8c94a4" mb={1}>
          {content.answer}
        </Typography>
        <TextField
          multiline
          rows={5}
          fullWidth
          placeholder={content.placeholder}
          value={currentAnswer}
          onChange={handleAnswerChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: INPUT_BG,
              color: '#ffffff',
              fontSize: '0.95rem',
              '& fieldset': {
                borderColor: DARK_BORDER
              },
              '&:hover fieldset': {
                borderColor: '#3b82f6'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6'
              }
            }
          }}
        />

        {/* Character Count */}
        <Box textAlign="right" mt={0.5} mb={3}>
          <Typography variant="caption" sx={{ color: '#8c94a4' }}>
            {currentAnswer.length} {currentAnswer.length !== 1 ? content.characters : content.character}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={currentStepIndex === 0}
            startIcon={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.8rem' }} />}
            sx={{
              borderRadius: '10px',
              color: '#ffffff',
              borderColor: DARK_BORDER,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: '#8c94a4'
              },
              '&.Mui-disabled': {
                borderColor: DARK_BORDER,
                color: '#475569'
              }
            }}
          >
            {content.previous}
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={
              <FontAwesomeIcon
                icon={currentStepIndex === totalSteps - 1 ? faCheck : faChevronRight}
                style={{ fontSize: '0.8rem' }}
              />
            }
            disabled={loading}
            sx={{
              borderRadius: '10px',
              backgroundColor: 'primary.main',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              py: 1,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 'none'
              }
            }}
          >
            {currentStepIndex === totalSteps - 1 ? (loading ? content.submitting : content.finish) : content.next}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TechnicalAssessmentForm;